import * as React from 'react';
import * as RPNInput from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';

import Input from './Input';
import { cn } from '@/app/lib/utils';
import CustomSelect, { Option } from './CustomSelect';

type PhoneInputProps = Omit<React.ComponentProps<'input'>, 'onChange' | 'value' | 'ref'> &
  Omit<RPNInput.Props<typeof RPNInput.default>, 'onChange'> & {
    onChange?: (value: RPNInput.Value) => void;
  };

const PhoneInput: React.ForwardRefExoticComponent<PhoneInputProps> = React.forwardRef<
  React.ElementRef<typeof RPNInput.default>,
  PhoneInputProps
>(({ className, onChange, value, ...props }, ref) => {
  return (
    <RPNInput.default
      ref={ref}
      className={cn('flex', className)}
      defaultCountry="US"
      flagComponent={FlagComponent}
      countrySelectComponent={CountrySelect}
      inputComponent={InputComponent}
      smartCaret={false}
      value={value || undefined}
      /**
       * Handles the onChange event.
       *
       * react-phone-number-input might trigger the onChange event as undefined
       * when a valid phone number is not entered. To prevent this,
       * the value is coerced to an empty string.
       *
       * @param {E164Number | undefined} value - The entered value
       */
      onChange={value => onChange?.(value || ('' as RPNInput.Value))}
      {...props}
    />
  );
});
PhoneInput.displayName = 'PhoneInput';

const InputComponent = React.forwardRef<HTMLInputElement, React.ComponentProps<'input'>>(
  ({ className, ...props }, ref) => (
    <Input
      wrapperClassName="w-full rounded-s-none"
      className={cn('w-full', className)}
      {...props}
      ref={ref}
    />
  )
);
InputComponent.displayName = 'InputComponent';

type CountryEntry = { label: string; value: RPNInput.Country | undefined };

type CountrySelectProps = {
  disabled?: boolean;
  value: RPNInput.Country;
  options: CountryEntry[];
  onChange: (country: RPNInput.Country) => void;
};

const CountrySelect = ({
  disabled,
  value: selectedCountry,
  options: countryList,
  onChange,
}: CountrySelectProps) => {
  const [searchValue, setSearchValue] = React.useState('');

  const options = React.useMemo(() => {
    return countryList
      .filter(
        ({ value, label }) => value && label.toLowerCase().includes(searchValue.toLowerCase())
      )
      .map(({ value, label }) => ({
        id: value?.toString() || '1',
        value: label,
      }));
  }, [countryList, searchValue]);

  console.log(selectedCountry);

  return (
    <CustomSelect
      defaultValue={selectedCountry || 'US'}
      disabled={disabled}
      options={options}
      searchable
      searchKeyword={searchValue}
      searchImmediate
      onSearch={setSearchValue}
      onOptionSelect={(option: any) => {
        if (option && option.id) {
          onChange(option.id);
        }
      }}
      optionItem={(option: Option) => {
        return (
          <div className="flex items-center gap-2">
            <FlagComponent country={option.id as any} countryName={option.value.toString()} />
            <span>{option.value}</span>
          </div>
        );
      }}
      triggerTextRender={(option: any) => {
        if (!option) {
          return '';
        }
        return (
          <div className="flex items-center gap-2">
            <FlagComponent country={option.id} countryName={option.value.toString()} />
          </div>
        );
      }}
      triggerClassNames="border-r-0 rounded-r-none h-[38px]"
    ></CustomSelect>
  );
};

const FlagComponent = ({ country, countryName }: RPNInput.FlagProps) => {
  const Flag = flags[country];

  return (
    <span className="flex h-4 w-6 overflow-hidden rounded-sm bg-strong-950/20 [&_svg:not([class*='size-'])]:size-full">
      {Flag && <Flag title={countryName} />}
    </span>
  );
};

export { PhoneInput };
